import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/motion";
import { MapPin, Video, Briefcase, GraduationCap, HeartHandshake } from "lucide-react";

const options = [
  {
    icon: MapPin,
    tag: "Δια ζώσης",
    title: "Στην Πάτρα",
    desc: "Σε έναν ζεστό, διακριτικό και φιλόξενο χώρο στη Ρήγα Φεραίου 119. Ιδανικό για όσους προτιμούν την προσωπική επαφή και την αίσθηση «εδώ και τώρα».",
    img: "https://customer-assets.emergentagent.com/job_therapy-anna-gr/artifacts/x1d5u8wb_IMG_7562.jpeg",
  },
  {
    icon: Video,
    tag: "Online",
    title: "Όπου κι αν βρίσκεσαι",
    desc: "Εξ αποστάσεως συνεδρίες με πλήρη εμπιστευτικότητα. Ευελιξία στον χρόνο και υποστήριξη ακόμη κι αν ζεις εκτός Πάτρας ή ταξιδεύεις.",
    img: "https://images.unsplash.com/photo-1593642634402-b0eb5e2eebc9?auto=format&fit=crop&q=80&w=1400",
  },
];

export default function Sessions() {
  return (
    <section
      id="sessions"
      data-testid="sessions-section"
      className="py-24 sm:py-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20">
        <div className="max-w-[820px] mb-16">
          <motion.p
            {...fadeUp}
            className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-5"
          >
            Συνεδρίες
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 }}
            data-testid="sessions-heading"
            className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[60px] leading-[1.05]"
          >
            Δια ζώσης ή <span className="italic font-light">online.</span>
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-6 font-body font-light text-lg text-foreground/70 leading-relaxed"
          >
            Επίλεξε το πλαίσιο που σου ταιριάζει. Και οι δύο μορφές διέπονται από
            τους ίδιους κανόνες δεοντολογίας, ασφάλειας και εμπιστευτικότητας.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {options.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.div
                key={o.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`session-card-${i}`}
                className="group relative rounded-3xl overflow-hidden border border-border/60 bg-card aspect-[4/5] sm:aspect-[16/12]"
              >
                <img
                  src={o.img}
                  alt={`${o.title} — ${o.tag} ψυχοθεραπεία`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/30 to-transparent" />

                <div className="absolute inset-0 p-8 sm:p-10 flex flex-col justify-between">
                  <div className="inline-flex items-center gap-2 self-start bg-background/85 backdrop-blur-md rounded-full px-4 py-2 text-foreground">
                    <Icon size={14} strokeWidth={1.5} />
                    <span className="text-[10px] tracking-[0.22em] uppercase font-body font-medium">
                      {o.tag}
                    </span>
                  </div>
                  <div className="text-background">
                    <h3 className="font-heading font-medium text-3xl sm:text-4xl tracking-tight">
                      {o.title}
                    </h3>
                    <p className="mt-3 font-body font-light text-base sm:text-lg leading-relaxed text-background/85 max-w-[440px]">
                      {o.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Special rates */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          data-testid="special-rates"
          className="mt-12 sm:mt-16 bg-card border border-border/50 rounded-3xl p-8 sm:p-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
                Ειδικές τιμές
              </p>
              <h3 className="font-heading font-medium text-foreground text-3xl sm:text-4xl tracking-tight leading-[1.1]">
                Πρόσβαση <span className="italic font-light">για όλους.</span>
              </h3>
              <p className="mt-5 font-body font-light text-foreground/75 leading-relaxed">
                Πιστεύω βαθιά ότι η ψυχοθεραπεία πρέπει να είναι προσιτή. Για
                αυτό προσφέρω ειδική τιμολόγηση σε άτομα που βρίσκονται σε πιο
                ευάλωτη οικονομικά θέση.
              </p>
              <p className="mt-3 font-body font-light text-sm text-muted-foreground">
                Παρακαλώ επικοινώνησε για το ακριβές κόστος και την προσκόμιση
                δικαιολογητικών.
              </p>
            </div>

            <motion.div
              {...stagger(0.08)}
              className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4"
            >
              {[
                { icon: Briefcase, label: "Άνεργοι", note: "Με κάρτα ανεργίας ΟΑΕΔ" },
                { icon: GraduationCap, label: "Φοιτητές", note: "Με ακαδημαϊκή ταυτότητα" },
                { icon: HeartHandshake, label: "ΑμεΑ", note: "Με βεβαίωση αναπηρίας" },
              ].map((g) => {
                const Icon = g.icon;
                return (
                  <motion.div
                    key={g.label}
                    variants={{
                      initial: { opacity: 0, y: 20 },
                      whileInView: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
                      },
                    }}
                    data-testid={`rate-${g.label}`}
                    className="bg-background border border-border/50 rounded-2xl p-6 flex flex-col gap-3"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      <Icon size={18} strokeWidth={1.5} />
                    </div>
                    <p className="font-heading text-xl text-foreground tracking-tight">
                      {g.label}
                    </p>
                    <p className="text-xs font-body text-muted-foreground leading-relaxed">
                      {g.note}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
